/**
 * Get PAC (Power AntiCrystallizer) contributor factor for a component
 * @param {string} component - Component name (sucrose, glucose, fructose, etc.)
 * @returns {number} PAC contributor factor
 */
export const getPacContributor = (component) => {
  switch (component) {
    case 'sucrose':
      return 1.0;
    case 'glucose':
      return 1.9;
    case 'fructose':
      return 1.9;
    case 'lactose':
      return 1.0;
    case 'maltose':
      return 1.0;
    case 'maltotriose':
      return 0.65;
    case 'galactose':
      return 1.9;
    case 'trehalose':
      return 1.0;
    case 'alcohol':
      return 7.4;
    case 'salt':
      return 5.85;
    default:
      return 0.0;
  }
};

/**
 * Get POD (Power of Depression) contributor factor for a component
 * @param {string} component - Component name (sucrose, glucose, fructose, etc.)
 * @returns {number} POD contributor factor
 */
export const getPodContributor = (component) => {
  switch (component) {
    case 'sucrose':
      return 1.0;
    case 'glucose':
      return 0.75;
    case 'fructose':
      return 1.7;
    case 'lactose':
      return 0.16;
    case 'maltose':
      return 0.4;
    case 'maltotriose':
      return 0.3;
    case 'galactose':
      return 0.35;
    case 'trehalose':
      return 0.45;
    default:
      return 0.0;
  }
};

/**
 * Calculate PAC (Power AntiCrystallizer) for an ingredient
 * @param {Object} args - Ingredient properties with sugar components, alcohol, salt, etc.
 * @returns {number} Calculated PAC value
 */
export const calculatePAC = (args) => {
  const components = [
    'sucrose',
    'glucose',
    'fructose',
    'lactose',
    'maltose',
    'maltotriose',
    'galactose',
    'trehalose',
    'salt',
    'alcohol'
  ];
  
  let pac = 0;
  let specificSugars = 0;
  
  components.forEach((component) => {
    const contributor = getPacContributor(component);
    pac += (args[component] || 0) * contributor;
    
    // Track specific sugars (exclude salt and alcohol)
    if (['sucrose', 'glucose', 'fructose', 'lactose', 'maltose', 'maltotriose', 'galactose', 'trehalose'].includes(component)) {
      specificSugars += args[component] || 0;
    }
  });
  
  // Add remaining sugars (if total sugars > specific sugars)
  if (args.sugars && args.sugars > specificSugars) {
    const remainingSugars = Math.max(0, args.sugars - specificSugars);
    pac += remainingSugars * 1.3; // Assume average sugar factor
  }
  
  return pac;
};

/**
 * Calculate POD (Power of Depression) for an ingredient
 * @param {Object} args - Ingredient properties with sugar components
 * @returns {number} Calculated POD value
 */
export const calculatePOD = (args) => {
  const components = [
    'sucrose',
    'glucose',
    'fructose',
    'lactose',
    'maltose',
    'maltotriose',
    'galactose',
    'trehalose'
  ];

  let specificSugars = 0;
  let pod = 0;
  
  components.forEach((component) => {
    const contributor = getPodContributor(component);
    pod += (args[component] || 0) * contributor;
    specificSugars += args[component] || 0;
  });
  
  // Add remaining sugars (if total sugars > specific sugars)
  if (args.sugars && args.sugars > specificSugars) {
    const remainingSugars = Math.max(0, args.sugars - specificSugars);
    pod += remainingSugars * 1.0; // Assume average sugar factor
  }
  
  return pod;
};

/**
 * Calculate both PAC and POD for an ingredient
 * @param {Object} args - Ingredient properties
 * @returns {Object} Object with pac and pod values
 */
export const calculatePACAndPOD = (args) => {
  return {
    pac: calculatePAC(args),
    pod: calculatePOD(args)
  };
};
